import { useEffect, useRef, useSyncExternalStore } from 'react';
import { fetchKey, useQuery } from './query';
import { detectNewMessage } from '../../shared/supportPolicy';

/**
 * Admin inbox alerts (pt-BR UI): while the internal admin is open, a new store message plays a short
 * sound (WebAudio, no asset) and shows a browser notification when the browser allows it. Opt-in with
 * "Ativar avisos com som"; the choice is remembered on this browser. Never fires for messages that were
 * already there when the page opened. The WhatsApp alert (server) is independent of this.
 */

/** The admin's inbox pulse: unread, overdue/warning threads and the newest store message. */
export const ADMIN_PULSE_PATH = '/api/admin/requests/unread';
export const PULSE_INTERVAL_MS = 30_000;
const PREF_KEY = 'perzo:admin:support-alerts';

export interface AdminPulse {
  unread: number;
  overdue?: number;
  warning?: number;
  latestMessageAt?: string | null;
}

// ── the remembered choice (this browser only; storage may be unavailable) ──

const listeners = new Set<() => void>();
let cached: boolean | null = null;

function readPref(): boolean {
  if (cached !== null) return cached;
  try {
    cached = window.localStorage.getItem(PREF_KEY) === 'on';
  } catch {
    cached = false;
  }
  return cached;
}

export function setAlertsEnabled(on: boolean): void {
  cached = on;
  try {
    window.localStorage.setItem(PREF_KEY, on ? 'on' : 'off');
  } catch {
    /* private mode: on for this page only */
  }
  listeners.forEach((fn) => fn());
}

export function useAlertsEnabled(): boolean {
  return useSyncExternalStore(
    (fn) => {
      listeners.add(fn);
      return () => listeners.delete(fn);
    },
    readPref,
    () => false,
  );
}

// ── sound and notification ──

let audio: AudioContext | null = null;

/** A short two-tone chime. Browsers start audio only after a click on the page; until then it is silent. */
export function playChime(): void {
  try {
    const Ctor = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) return;
    audio ??= new Ctor();
    if (audio.state === 'suspended') void audio.resume().catch(() => undefined);
    const t = audio.currentTime;
    [880, 1320].forEach((freq, i) => {
      const osc = audio!.createOscillator();
      const gain = audio!.createGain();
      osc.type = 'sine';
      osc.frequency.value = freq;
      const start = t + i * 0.16;
      gain.gain.setValueAtTime(0.0001, start);
      gain.gain.exponentialRampToValueAtTime(0.18, start + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, start + 0.28);
      osc.connect(gain).connect(audio!.destination);
      osc.start(start);
      osc.stop(start + 0.3);
    });
  } catch {
    /* no audio on this browser */
  }
}

export function notificationPermission(): NotificationPermission | 'unsupported' {
  return typeof window !== 'undefined' && 'Notification' in window ? Notification.permission : 'unsupported';
}

/** Turns the alerts on from a click: unlocks audio (one chime as a check) and asks for notifications once. */
export async function enableAlerts(): Promise<NotificationPermission | 'unsupported'> {
  setAlertsEnabled(true);
  playChime();
  const current = notificationPermission();
  if (current === 'default') {
    try {
      return await Notification.requestPermission();
    } catch {
      return notificationPermission();
    }
  }
  return current;
}

function showNotification(onOpen: () => void): void {
  if (notificationPermission() !== 'granted') return;
  try {
    // No store text in it: the message is untrusted and stays inside the admin.
    const n = new Notification('Nova mensagem de lojista', { body: 'Abra Suporte no admin Perzo para responder.', tag: 'perzo-support' });
    n.onclick = () => {
      window.focus();
      onOpen();
      n.close();
    };
  } catch {
    /* some browsers only notify from a service worker */
  }
}

/**
 * Mounted by the admin shell. Polls the pulse every 30 s while alerts are on (also in a background tab,
 * which the nav badge's visible-only polling skips) and alerts once per newer store message.
 */
export function useSupportAlerts(enabled: boolean, onOpen: () => void): void {
  const on = useAlertsEnabled() && enabled;
  const pulse = useQuery<AdminPulse>(enabled ? ADMIN_PULSE_PATH : null, { refetchInterval: PULSE_INTERVAL_MS });
  const baseline = useRef<string | null | undefined>(undefined);
  const open = useRef(onOpen);
  open.current = onOpen;

  useEffect(() => {
    if (!on) return;
    const timer = window.setInterval(() => {
      if (document.visibilityState !== 'visible') void fetchKey(ADMIN_PULSE_PATH);
    }, PULSE_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [on]);

  const latest = pulse.data ? (pulse.data.latestMessageAt ?? null) : undefined;
  useEffect(() => {
    if (latest === undefined) return;
    const next = detectNewMessage(baseline.current, latest);
    baseline.current = next.baseline;
    if (next.alert && on) {
      playChime();
      showNotification(() => open.current());
    }
  }, [latest, on]);
}
